import type {
  ApplyPromoCodeRequest,
  PromoCodeApplyChannel,
} from 'pi-kiosk-shared/promo/ApplyPromoCodeRequest';
import type { ApplyPromoCodeResponse } from 'pi-kiosk-shared/promo/ApplyPromoCodeResponse';
import type { RemovePromoCodeRequest } from 'pi-kiosk-shared/promo/RemovePromoCodeRequest';
import { buildPromoApplyAcceptLanguage } from 'pi-kiosk-shared';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { getOrCreateCustomerPromoDeviceId } from './customerPromoDeviceId.js';
import { rewardsLog } from './logging.js';

/**
 * Customer PWA gateway for typed promo-code apply/remove (ADR-014).
 * Canonical routes: POST /api/v1/promotions/apply-code | remove-code
 * (ApiClient resolves to /api/{tenant}/v1/…).
 * Channel is always CUSTOMER_CHECKOUT for checkout typed apply.
 * Body/response contracts: pi-kiosk-shared Apply/RemovePromoCode*.
 * G14: no customer `/validate-code` UI — product Spec is apply-only (opaque validate stays API/ops).
 */

export const CUSTOMER_PROMO_CODE_APPLY_CHANNEL =
  'CUSTOMER_CHECKOUT' as const satisfies PromoCodeApplyChannel;

/** Transport envelope: shared body fields + client idempotency / device headers. */
export type CustomerApplyPromoCodeInput = Omit<ApplyPromoCodeRequest, 'channel'> & {
  readonly idempotencyKey: string;
  readonly deviceId?: string | null;
  readonly signal?: AbortSignal;
  /** UI locale for Accept-Language (defaults to `cs` when omitted). */
  readonly acceptLanguage?: string | null;
};

/** Transport envelope: shared body fields + client idempotency / device headers. */
export type CustomerRemovePromoCodeInput = Omit<RemovePromoCodeRequest, 'channel'> & {
  readonly idempotencyKey: string;
  readonly deviceId?: string | null;
};

type CustomerApi = ReturnType<typeof useApiClient>;

function unwrapApplyResponse(
  response: { data?: ApplyPromoCodeResponse } | ApplyPromoCodeResponse,
): ApplyPromoCodeResponse {
  if (
    response != null &&
    typeof response === 'object' &&
    'activatedPromoRewardId' in response &&
    typeof (response as ApplyPromoCodeResponse).activatedPromoRewardId === 'string'
  ) {
    return response as ApplyPromoCodeResponse;
  }
  const nested = (response as { data?: ApplyPromoCodeResponse }).data;
  if (nested != null && typeof nested.activatedPromoRewardId === 'string') {
    return nested;
  }
  throw new Error('Apply promo code response missing activatedPromoRewardId');
}

function resolveDeviceId(explicit?: string | null): string {
  const trimmed = explicit?.trim();
  if (trimmed != null && trimmed.length > 0) {
    return trimmed;
  }
  return getOrCreateCustomerPromoDeviceId();
}

/**
 * POST /api/v1/promotions/apply-code — body: checkoutSessionPublicId + code + channel CUSTOMER_CHECKOUT.
 */
export async function applyPromoCode(
  api: CustomerApi,
  input: CustomerApplyPromoCodeInput,
): Promise<ApplyPromoCodeResponse> {
  const body: ApplyPromoCodeRequest = {
    checkoutSessionPublicId: input.checkoutSessionPublicId,
    code: input.code,
    channel: CUSTOMER_PROMO_CODE_APPLY_CHANNEL,
  };
  try {
    const response = await api.request<{ data: ApplyPromoCodeResponse } | ApplyPromoCodeResponse>(
      '/promotions/apply-code',
      {
        method: 'POST',
        body,
        idempotencyKey: input.idempotencyKey,
        signal: input.signal,
        headers: {
          'X-Device-Id': resolveDeviceId(input.deviceId),
          'Accept-Language': buildPromoApplyAcceptLanguage(
            input.acceptLanguage ?? undefined,
          ),
        },
      },
    );
    return unwrapApplyResponse(response);
  } catch (error) {
    rewardsLog.warn('Promo code apply failed', {
      operation: 'applyPromoCode',
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

/**
 * POST /api/v1/promotions/remove-code — body: checkoutSessionPublicId + channel CUSTOMER_CHECKOUT.
 */
export async function removePromoCode(
  api: CustomerApi,
  input: CustomerRemovePromoCodeInput,
): Promise<void> {
  const body: RemovePromoCodeRequest = {
    checkoutSessionPublicId: input.checkoutSessionPublicId,
    channel: CUSTOMER_PROMO_CODE_APPLY_CHANNEL,
  };
  try {
    await api.request('/promotions/remove-code', {
      method: 'POST',
      body,
      idempotencyKey: input.idempotencyKey,
      headers: { 'X-Device-Id': resolveDeviceId(input.deviceId) },
    });
  } catch (error) {
    rewardsLog.warn('Promo code remove failed', {
      operation: 'removePromoCode',
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}
